import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, DateFilterModule, } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../lib/axios';
import { Box, Typography, CircularProgress, Tooltip, IconButton, Button } from '@mui/material';
import { usePermissions } from '../../GlobalFunction/userPermissions';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import '../../AgGridStyle.css';
import HomeIcon from '@mui/icons-material/Home';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule,]);

const EmailLog = () => {
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const UserId = sessionStorage?.getItem('UserId');
    const permissions = usePermissions("Payment Request Log");
    const { AddRec = "0" } = permissions || {};
    const gridRef = useRef();
    const navigate = useNavigate();
    const globalProperty = useSelector(selectGlobalProperty);
    const currentYear = dayjs().year();
    const previousYear = currentYear - 1;
    const [dateFrom, setDateFrom] = useState(dayjs(`${previousYear}-04-01`));
    const [dateTo, setDateTo] = useState(dayjs(`${currentYear}-04-01`).subtract(1, 'day'));

    const colDefs = useMemo(() => [
        { field: 'PropName', headerName: 'Property', width: 200 },
        { field: 'UserName', headerName: 'UserName', width: 150 },
        { field: 'FromEmail', headerName: 'FromEmail', width: 150 },
        { field: 'TblName', headerName: 'TblName', width: 220 },
        { field: 'EmailType', headerName: 'EmailType', width: 200 },
        { field: 'Msg', headerName: 'Msg', width: 500 },
    ], []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        width: 100,
        tooltipValueGetter: (params) => params.value ?? '',
        copyable: true
    }), []);

    useEffect(() => {
        fetchEmailReqLog()
    }, [globalProperty]);

    const fetchEmailReqLog = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.post(`EmailRequest/GetEmailLogs`, {
                PageNumber: 1,
                PageSize: 1,
                SearchText: "",
                PropId: globalProperty,
                SearchFrom: dayjs(dateFrom).format('YYYY-MM-DD'),
                SearchTo: dayjs(dateTo).format('YYYY-MM-DD'),
                Flag: ''
            });

            if (response.data.Status === 0) {
                const formattedData = response.data.Data.map(emailLog => ({
                    ...emailLog,
                    CreatedDate: emailLog.CreatedDate ? dayjs(emailLog.CreatedDate).format('DD-MM-YYYY HH:mm:ss') : '',
                }));
                setRowData(formattedData);
            } else {
                setRowData([]);
                toast.info(response.data.Data);
            }
        } catch (error) {
            toast.error('Error fetching payment data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    async function handleFetchedData() {
        await fetchEmailReqLog();
    };

    const onGridReady = useCallback((params) => {
        // No sizeColumnsToFit here to preserve flex sizing
    }, []);

    const handleClearFilters = () => {
        gridRef.current?.api?.setFilterModel(null);
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '90vh',
                padding: '10px',
                backgroundColor: 'rgb(249, 249, 249)',
                position: 'relative'
            }}
        >
            <ToastContainer />
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "8px 16px", sm: "12px 16px" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "95%",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: 2,
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={() => navigate('/utilities')}
                    >
                        Utilities & Other
                    </Link>

                    <Typography color="text.primary" sx={{ fontWeight: "500" }}>
                        Email Logs
                    </Typography>
                </Breadcrumbs>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: { xs: 1, sm: 1, md: 0 },
                        flexShrink: 0,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <DatePicker
                                    label="From-Date"
                                    value={dateFrom}
                                    onChange={(newValue) => setDateFrom(newValue)}
                                    format="DD/MM/YYYY"
                                    views={['day', 'month', 'year']}
                                    sx={{ width: 150 }}
                                />
                                <DatePicker
                                    label="To-Date"
                                    value={dateTo}
                                    onChange={(newValue) => setDateTo(newValue)}
                                    format="DD/MM/YYYY"
                                    views={['day', 'month', 'year']}
                                    sx={{ width: 150 }}
                                />
                            </Box>
                        </LocalizationProvider>
                        <Tooltip title='Retreive data' arrow>
                            <Button
                                variant='contained'
                                onClick={handleFetchedData}
                                sx={{
                                    borderRadius: '20px', backgroundColor: '#635bff', '&:hover': {
                                        backgroundColor: '#635bff',
                                    },
                                }}
                            >
                                Get Data
                            </Button>
                        </Tooltip>
                    </Box>

                    <Tooltip title='Clear all applied filter' arrow>
                        <Button
                            variant="outlined"
                            onClick={handleClearFilters}
                            size="small"
                            sx={{
                                borderRadius: "50px",
                                textTransform: "none",
                                borderColor: "#635bff",
                                color: "#635bff",
                                "&:hover": {
                                    borderColor: "#524ccc",
                                    backgroundColor: "rgba(99, 91, 255, 0.04)",
                                }
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Tooltip>

                    <Tooltip title='Previous Page' arrow>
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            justifyContent: "flex-start"
                        }}
                        >
                            <IconButton
                                onClick={() => navigate('/utilities')}
                                sx={{
                                    backgroundColor: '#635bff',
                                    borderRadius: '50%',
                                    width: 35,
                                    height: 35,
                                    '&:hover': {
                                        backgroundColor: '#635bff',
                                    },
                                    boxShadow: 3,
                                }}
                            >
                                <ArrowBackIcon sx={{ color: 'white' }} />
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            <Box className="table-grid ag-theme-alpine"
                sx={{ height: 'calc(97% - 56px)', width: '95%', margin: '0 auto', position: 'relative' }}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            textAlign: 'center',
                        }}
                    >
                        <CircularProgress size="3rem" sx={{ color: '#635bff' }} />

                        <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
                            Loading data...
                        </Typography>
                    </Box>
                ) : (
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        pagination={true}
                        paginationPageSize={1000}
                        paginationPageSizeSelector={[100, 500, 1000, 5000]}
                        onGridReady={onGridReady}
                        enableCellTextSelection={true}
                        rowHeight={25}
                        headerHeight={30}
                        floatingFiltersHeight={28}
                        overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No rows to show</span>'}
                        tooltipShowDelay={0}
                        getRowStyle={(params) => ({
                            backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff'
                        })}
                    />
                )}
            </Box>
            <Box sx={{
                position: 'absolute',
                bottom: 12,
                left: 60,
                fontSize: '14px',
                color: 'black',
            }}>
                Total Records: <b>{rowData.length}</b>
            </Box>
        </Box>
    );
};

export default EmailLog;
